import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  arrayUnion,
  deleteField,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, app } from "../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import './CalendarStyles.css'; // Custom CSS for better styles
import "./JobDetailsShared.css";
import { useNavigate } from 'react-router-dom';
import {
  applyOccurrenceOverrideToRenderedJob,
  getOccurrenceOverrideRenderJobs,
  mergeRenderedRecurringJobs,
  saveRecurringJobEdit,
  getRecurrenceEndKey,
  getExcludedDateSet,
  closeOrDeleteJob,
} from "../utils/recurringEditUtils";
import { addJobTotal } from "../utils/addJobTotal";
import {
  refreshKeepingView,
  restoreScrollAfterRefresh,
  getCompletionOccurrenceKey,
  occurrenceIsInDateList,
} from "../utils/recurringOccurrenceUtils";
import {
  getMaterialsGroupId,
  fetchAllJobMaterials,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";
import QuickJobFieldEditor from "../components/QuickJobFieldEditor";
import CollapsibleActionSection from "../components/CollapsibleActionSection";
import JobHistoryPopup from "../components/JobHistoryPopup";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import { userHasVisitForJob } from "../clock/jobVisits";
import AvailabilityView from "../calendar/AvailabilityView";
import Scheduling from "./Scheduling";
import { buildTimeOffByDate, shouldShowOnCalendar } from "../utils/timeOffUtils";
import { TimeOffDetailsModal } from "../components/TimeOffCalendarPieces";


const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes jmc-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "jmc-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

const CalendarPage = () => {
  const [jobs, setJobs] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  // Completing a job reloads the page so every list re-reads Firestore.
  // The view + focused date are persisted so that reload lands the user
  // exactly where they were — if they were looking at the Calendar in week
  // view on a given date, that's what comes back, not a reset to today/month.
  const CALENDAR_VIEW_KEY = "priority_calendar_view";
  const CALENDAR_DATE_KEY = "priority_calendar_date";

  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem(CALENDAR_DATE_KEY);
      const parsed = stored ? moment(stored, "YYYY-MM-DD", true) : null;
      if (parsed && parsed.isValid()) return parsed.toDate();
    } catch (e) {
      // sessionStorage unavailable — fall through to today
    }
    return new Date();
  });

  const [view, setView] = useState(() => {
    try {
      const stored = sessionStorage.getItem(CALENDAR_VIEW_KEY);
      if (["day", "week", "month", "availability"].includes(stored)) return stored;
    } catch (e) {
      // ignore
    }
    return 'month';
  }); // Default to 'month' view

  useEffect(() => {
    try {
      sessionStorage.setItem(CALENDAR_VIEW_KEY, view);
      sessionStorage.setItem(
        CALENDAR_DATE_KEY,
        moment(selectedDate).format("YYYY-MM-DD")
      );
    } catch (e) {
      // ignore
    }
  }, [view, selectedDate]);

  useEffect(() => {
    restoreScrollAfterRefresh();
  }, []);
  const [selectedJob, setSelectedJob] = useState(null); // For displaying job details

  // Lock background scrolling while the Job Details popup is open so the
  // popup can't be scrolled past — it has to be closed to interact with
  // the page behind it.
  useEffect(() => {
    if (!selectedJob) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedJob]);
  const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState(null); // For displaying time off details
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [userFullName, setUserFullName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState(null); // Availability -> Schedule Job
  const [currentUid, setCurrentUid] = useState(null);
  // Set once the Complete Job & Create Invoice form is submitted.
  const [completionSubmitted, setCompletionSubmitted] = useState(false);
  const [propertiesList, setPropertiesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
  const [materialsDraft, setMaterialsDraft] = useState([]);
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptNote, setReceiptNote] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [showTotalPopup, setShowTotalPopup] = useState(false);
  const [totalInput, setTotalInput] = useState("");
  const [jobTotals, setJobTotals] = useState({});
  const getJobTotal = (job) =>
    jobTotals[String(job?.wo ?? job?.id ?? "")] ?? job?.estimatedTotal ?? 0;
  const [jobMaterials, setJobMaterials] = useState({});
  const getMaterialsForJob = (job) =>
    jobMaterials[getMaterialsGroupId(job)] || [];
  const navigate = useNavigate();

  const functions = getFunctions(app, "us-central1");
  const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");

  const timeWindowOptions = [
    "ANY",
    "8AM - 10AM",
    "9AM - 11AM",
    "10AM - 12PM",
    "11AM - 1PM",
    "12PM - 2PM",
    "1PM - 3PM",
    "2PM - 4PM",
    "3PM - 5PM",
    "4PM - 6PM",
    "Custom Time"
  ];
  

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsRef = collection(db, 'jobs');
        const querySnapshot = await getDocs(jobsRef);
        const jobsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setJobs(jobsData);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    const fetchJobTotals = async () => {
      try {
        const totalsSnap = await getDocs(collection(db, "job_totals"));
        const totalsMap = {};
        totalsSnap.forEach((doc) => {
          totalsMap[doc.id] = doc.data().total;
        });
        setJobTotals(totalsMap);
      } catch (error) {
        console.error('Error fetching job totals:', error);
      }
    };

    const loadAllJobMaterials = async () => {
      try {
        setJobMaterials(await fetchAllJobMaterials());
      } catch (error) {
        console.error('Error fetching job materials:', error);
      }
    };

    const fetchTimeOffRequests = async () => {
      try {
        const timeOffRef = collection(db, 'timeOff');
        const querySnapshot = await getDocs(timeOffRef);
        const requests = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Approved requests + logged call-outs / left early / came in late.
        setTimeOffRequests(requests.filter(shouldShowOnCalendar));
      } catch (error) {
        console.error('Error fetching time off requests:', error);
      }
    };

    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        setCurrentUid(currentUser.uid);
        if (userDoc) {
          setUserFullName(`${userDoc.data().firstName} ${userDoc.data().lastName}`);
          setUserRole(userDoc.data().role || null);
        }
      }
    };

   
    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, 'properties');
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPropertiesList(propertiesData);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsersList(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchJobs();
    fetchJobTotals();
    loadAllJobMaterials();
    fetchTimeOffRequests();
    fetchUserData();
    fetchProperties();
    fetchUsers();
  }, []);

  const toggleWeek = (direction) => {
    setSelectedDate((prev) =>
      direction === 'prev'
        ? moment(prev).subtract(7, 'days').toDate()
        : moment(prev).add(7, 'days').toDate()
    );
  };

  const toggleDay = (direction) => {
    setSelectedDate((prev) =>
      direction === 'prev'
        ? moment(prev).subtract(1, 'day').toDate()
        : moment(prev).add(1, 'day').toDate()
    );
  };

  const toggleMonth = (direction) => {
    setSelectedDate((prev) =>
      direction === 'prev'
        ? moment(prev).subtract(1, 'month').toDate()
        : moment(prev).add(1, 'month').toDate()
    );
  };

  
  const goToToday = () => {
    setSelectedDate(new Date());
  };
  
  const toDateKey = (v) => {
    if (!v) return "";
    const m = moment(v?.toDate ? v.toDate() : v);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };
  
  const toMomentDate = (v) => {
    if (!v) return null;
    const m = moment(v?.toDate ? v.toDate() : v);
    return m.isValid() ? m.startOf("day") : null;
  };
  
  const getRecurrenceValue = (job) =>
    (job.recurrence || job.reccurance || job.recurring || job.repeat || "").toString().trim();
  
  const getRecurrenceType = (job) => {
    let type = "none";
  
    if (job?.recurrence && typeof job.recurrence === "object") {
      type = job.recurrence.type || "none";
    } else {
      type =
        job?.reccurance ??
        job?.recurring ??
        job?.repeat ??
        job?.recurrence ??
        "none";
    }
  
    type = type.toString().toLowerCase().trim();
  
    if (!type || type === "one-time" || type === "onetime") return "none";
    if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
    if (type === "bi-monthly" || type === "bi monthly") return "bimonthly";
    if (type === "bi-yearly" || type === "bi yearly") return "biyearly";
    if (type === "customdates" || type === "custom dates") return "customdates";
  
    return type;
  };
  
  const isRecurringJob = (job) => {
    const type = getRecurrenceType(job);
    return type && type !== "none";
  };
  
  
  const getOccurrenceDateKey = (job) => {
    if (isRecurringJob(job)) {
      return toDateKey(job.occurrenceDate || job.startDate);
    }
  
    return toDateKey(job.startDate);
  };

  // Checks the occurrence's canonical (series) date and, as a fallback, the
  // day it was rescheduled to. getOccurrenceDateKey() only knew about the
  // latter, so a moved occurrence never went red after being completed.
  const isOccurrenceCompleted = (job) =>
    occurrenceIsInDateList(job, job.completedDates);

  const isOccurrenceInvoiced = (job) =>
    occurrenceIsInDateList(job, job.invoicedDates);
  
  const isOccurrenceClosed = (job) => {
    if (isRecurringJob(job)) {
      return isOccurrenceCompleted(job) || isOccurrenceInvoiced(job);
    }
  
    const status = (job?.status || "").trim().toLowerCase();
    return status === "completed" || status === "invoice sent";
  };

  const getWeekStart = (date) => moment(date).day(0).startOf("day");
  const getWeekEnd = (date) => moment(date).day(6).endOf("day");

  const getJobTileColor = (job) => {
    if (isOccurrenceClosed(job)) return "#D96C6C"; // soft warm red override
  
    const type = (job?.serviceType || "").toLowerCase().trim();
  
    if (type === "proposal") return "#6FAE7C";       // warm soft green
    if (type === "meeting") return "#D9B95B";        // warm muted yellow
    if (type === "inspection") return "#D9B95B";     // warm muted yellow
    if (type === "action item" || type === "action items") return "#8E6BBE"; // warm purple
  
    return job.color || "#5B8FD9";                   // soft warm blue
  };

  const getJobDisplayText = (job) =>
    `${isOccurrenceClosed(job) ? "✕ " : ""}${job.siteName || "Untitled Job"}`;

  const baseJobs = useMemo(() => {
    const seen = new Map();
  
    jobs.forEach((job) => {
      if (!job?.id) return;
  
      if (!seen.has(job.id)) {
        const cleanJob = { ...job };
        delete cleanJob.__renderDate;
        delete cleanJob.occurrenceDate;
        seen.set(job.id, cleanJob);
      }
    });
  
    return Array.from(seen.values());
  }, [jobs]);
  
  const getCalendarWindow = () => {
    const today = moment().startOf("day");
  
    const visibleStart =
      view === "day"
        ? moment(selectedDate).startOf("day")
        : view === "week" || view === "availability"
        ? getWeekStart(selectedDate)
        : moment(selectedDate).startOf("month").startOf("week");
  
    const visibleEnd =
      view === "day"
        ? moment(selectedDate).endOf("day")
        : view === "week" || view === "availability"
        ? getWeekEnd(selectedDate)
        : moment(selectedDate).endOf("month").endOf("week");
  
    return {
      start: moment.min(visibleStart, today),
      end: moment.max(visibleEnd, today.clone().add(90, "days")),
    };
  };
  
  const expandJobIntoDays = (job) => {
    const baseStart = toMomentDate(job.startDate);
    const baseEnd = toMomentDate(job.endDate || job.startDate);
    const recurrenceType = getRecurrenceType(job);
  
    if (!baseStart || !baseEnd) return [];
  
    const days = [];
    const { start: windowStart, end: rawWindowEnd } = getCalendarWindow();

    // A series that has been ended (via "Close this job and all future
    // jobs", or its endsOn/endDate set some other way) must stop generating
    // occurrences after that date. Without this cap, an ended/split series
    // keeps rendering forever and overlaps with whatever replaced it -
    // showing as duplicate tiles on the same date.
    const recurrenceEndKey = getRecurrenceEndKey(job);
    const windowEnd =
      recurrenceType !== "none" && recurrenceEndKey
        ? moment.min(rawWindowEnd, moment(recurrenceEndKey, "YYYY-MM-DD").endOf("day"))
        : rawWindowEnd;

    // Occurrences individually closed via "Close this job only (this date)"
    // should stop appearing without affecting the rest of the series.
    const excludedDates = getExcludedDateSet(job);
  
    // Only ONE-TIME jobs use endDate to render a multi-day span. For
    // recurring jobs, endDate is how users mark "when should this job stop
    // repeating" - it is not a per-occurrence duration. Treating it as a
    // span here made every occurrence render across (endDate - startDate)
    // days, which overlaps the next occurrence's tiles and shows the same
    // job duplicated on shared days. Home.js already treats recurring
    // occurrences as single-day; this keeps Calendar consistent with it.
    const spanDays =
      recurrenceType === "none"
        ? Math.max(baseEnd.diff(baseStart, "days"), 0)
        : 0;
  
    // ONE-TIME JOBS
    // Example: startDate 5/27 and endDate 5/29 shows on 5/27, 5/28, 5/29
    if (recurrenceType === "none") {
      let current = baseStart.clone();
  
      while (current.isSameOrBefore(baseEnd, "day")) {
        if (current.isBetween(windowStart, windowEnd, "day", "[]")) {
          days.push({
            ...job,
            occurrenceDate: baseStart.format("YYYY-MM-DD"),
            __renderDate: current.format("YYYY-MM-DD"),
          });
        }
  
        current.add(1, "day");
      }
  
      return days;
    }
  
    // CUSTOM DATE RECURRING JOBS
    if (recurrenceType === "customdates") {
      (job.recurrence?.customDates || []).forEach((d) => {
        const occurrenceStart = toMomentDate(d);
        if (!occurrenceStart) return;
        if (excludedDates.has(occurrenceStart.format("YYYY-MM-DD"))) return;
  
        for (let i = 0; i <= spanDays; i++) {
          const renderDay = occurrenceStart.clone().add(i, "days");
  
          if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
            days.push({
              ...job,
              originalOccurrenceDate: occurrenceStart.format("YYYY-MM-DD"),
              occurrenceDate: occurrenceStart.format("YYYY-MM-DD"),
              startDate: occurrenceStart.format("YYYY-MM-DD"),
              endDate: occurrenceStart.clone().add(spanDays, "days").format("YYYY-MM-DD"),
              __renderDate: renderDay.format("YYYY-MM-DD"),
            });
          }
        }
      });
  
      return days;
    }
  
  // WEEKLY / BIWEEKLY
if (recurrenceType === "weekly" || recurrenceType === "biweekly") {
  const intervalWeeks = recurrenceType === "biweekly" ? 2 : 1;

  const selectedDays =
    Array.isArray(job.recurrence?.daysOfWeek) && job.recurrence.daysOfWeek.length
      ? job.recurrence.daysOfWeek.map(Number)
      : [baseStart.day()];

  let cursor = baseStart.clone();

  while (cursor.isSameOrBefore(windowEnd, "day")) {
    const weeksDiff = cursor
      .clone()
      .startOf("week")
      .diff(baseStart.clone().startOf("week"), "weeks");

    const inCycle = weeksDiff >= 0 && weeksDiff % intervalWeeks === 0;

    if (
      cursor.isSameOrAfter(windowStart, "day") &&
      inCycle &&
      selectedDays.includes(cursor.day()) &&
      !excludedDates.has(cursor.format("YYYY-MM-DD"))
    ) {
      for (let i = 0; i <= spanDays; i++) {
        const renderDay = cursor.clone().add(i, "days");

        if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
          days.push({
            ...job,
            originalOccurrenceDate: cursor.format("YYYY-MM-DD"),
            occurrenceDate: cursor.format("YYYY-MM-DD"),
            startDate: cursor.format("YYYY-MM-DD"),
            endDate: cursor.clone().add(spanDays, "days").format("YYYY-MM-DD"),
            __renderDate: renderDay.format("YYYY-MM-DD"),
          });
        }
      }
    }

    cursor.add(1, "day");
  }

  return days;
}
  
    // DAILY / MONTHLY / BIMONTHLY / QUARTERLY / BIYEARLY / YEARLY
    let cursor = baseStart.clone();
  
    while (cursor.isSameOrBefore(windowEnd, "day")) {
      if (
        cursor.isSameOrAfter(windowStart, "day") &&
        !excludedDates.has(cursor.format("YYYY-MM-DD"))
      ) {
        for (let i = 0; i <= spanDays; i++) {
          const renderDay = cursor.clone().add(i, "days");
  
          if (renderDay.isBetween(windowStart, windowEnd, "day", "[]")) {
            days.push({
              ...job,
              originalOccurrenceDate: cursor.format("YYYY-MM-DD"),
              occurrenceDate: cursor.format("YYYY-MM-DD"),
              startDate: cursor.format("YYYY-MM-DD"),
              endDate: cursor.clone().add(spanDays, "days").format("YYYY-MM-DD"),
              __renderDate: renderDay.format("YYYY-MM-DD"),
            });
          }
        }
      }
  
      if (recurrenceType === "daily") {
        cursor.add(1, "day");
      } else if (recurrenceType === "monthly") {
        cursor.add(1, "month");
      } else if (recurrenceType === "bimonthly") {
        cursor.add(2, "months");
      } else if (recurrenceType === "quarterly") {
        cursor.add(3, "months");
      } else if (recurrenceType === "biyearly") {
        cursor.add(6, "months");
      } else if (recurrenceType === "yearly") {
        cursor.add(1, "year");
      } else {
        break;
      }
    }
  
    return days;
  };
  
  const expandedJobs = useMemo(() => {
    const normalOccurrences = baseJobs
      .flatMap(expandJobIntoDays)
      .map(applyOccurrenceOverrideToRenderedJob);

    const movedOccurrences = baseJobs.flatMap(
      getOccurrenceOverrideRenderJobs
    );

    return mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ]);
  }, [baseJobs, selectedDate, view]);
  
  const jobsByDate = useMemo(() => {
    const map = {};
    const seen = new Set();
  
    // Techs only see the jobs they are assigned to (techAssign).
    const techOnly = userRole === "Tech";
    expandedJobs.forEach((job) => {
      const dateKey = job.__renderDate;
      if (!dateKey) return;

      if (techOnly) {
        const assigned = Array.isArray(job.techAssign)
          ? job.techAssign
          : [job.techAssign];
        if (!userFullName || !assigned.includes(userFullName)) return;
      }

      const recurring = isRecurringJob(job);
      const occurrenceKey = toDateKey(
        job.occurrenceDate || job.__renderDate || job.startDate
      );
      const recurringSeriesKey = [
        job.wo || job.workOrder || job.workOrderNumber || "",
        job.siteName || job.property || "",
        job.serviceType || "",
        toDateKey(job.originalSeriesStartDate || job.seriesStartDate || job.startDate),
        getRecurrenceType(job),
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .join("|");

      const sourceKey = recurring
        ? recurringSeriesKey || String(job.id || "job").trim().toLowerCase()
        : String(job.id || job.wo || "job").trim().toLowerCase();

      const originalOccurrenceKey = toDateKey(
        job.originalOccurrenceDate || job.occurrenceDate || job.__renderDate || job.startDate
      );
      const dedupeKey = `${sourceKey}_${originalOccurrenceKey || occurrenceKey}_${dateKey}`;

      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
  
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(job);
    });
  
    return map;
  }, [expandedJobs, userRole, userFullName]);

  const getJobRenderKey = (job, index = 0) => {
    return [
      job.id,
      job.__renderDate || "",
      job.occurrenceDate || "",
      job.startDate || "",
      job.endDate || "",
      job.wo || "",
      index,
    ].join("_");
  };

  const timeOffByDate = useMemo(
    () => buildTimeOffByDate(timeOffRequests),
    [timeOffRequests]
  );

  const getFilteredTimeOffRequests = () => {
    if (view === 'week') {
      const startOfWeek = getWeekStart(selectedDate);
      const endOfWeek = getWeekEnd(selectedDate);
      return timeOffRequests.filter((request) => {
        const startDate = moment(request.startDate);
        const endDate = moment(request.endDate || request.startDate);
        return startDate.isBetween(startOfWeek, endOfWeek, 'day', '[]') || endDate.isBetween(startOfWeek, endOfWeek, 'day', '[]');
      });
    }

    if (view === 'day') {
      return timeOffRequests.filter((request) => {
        const startDate = moment(request.startDate);
        const endDate = moment(request.endDate || request.startDate);
        return moment(selectedDate).isBetween(startDate, endDate, 'day', '[]');
      });
    }

    if (view === 'month') {
      const startOfMonth = moment(selectedDate).startOf('month');
      const endOfMonth = moment(selectedDate).endOf('month');
      return timeOffRequests.filter((request) => {
        const startDate = moment(request.startDate);
        const endDate = moment(request.endDate || request.startDate);
        return startDate.isBetween(startOfMonth, endOfMonth, 'day', '[]') || endDate.isBetween(startOfMonth, endOfMonth, 'day', '[]');
      });
    }

    return timeOffRequests;
  };

  const getJobBarStyle = (job) => {
    return {
      backgroundColor: getJobTileColor(job),
      color: "#fff",
      padding: "5px",
      borderRadius: "5px",
      cursor: "pointer",
      border: "1px solid rgba(255,255,255,0.18)",
      opacity: isOccurrenceClosed(job) ? 0.95 : 1,
      marginBottom: "4px",
      fontSize: "11px",
      lineHeight: "1.2",
      whiteSpace: "normal",
    };
  };

  const getTimeOffBarStyle = (request) => {
    const colors = ['red', 'orange', 'purple', 'green', 'blue'];
    const colorIndex = request.name ? request.name.charCodeAt(0) % colors.length : 0; // Assign color based on the first character of the name
    return {
      backgroundColor: colors[colorIndex],
      color: '#fff',
      padding: '5px',
      borderRadius: '5px',
      cursor: 'pointer',
    };
  };

  const closeModal = () => {
    setSelectedJob(null);
    setIsEditing(false);
  };

  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const markJobCompleted = async () => {
    if (!selectedJob) return;
  
    const jobRef = doc(db, "jobs", selectedJob.id);
  
    // Canonical (series) occurrence date — see getCompletionOccurrenceKey.
    // Using the rescheduled date here is what desynced completedDates from
    // every expander.
    const occurrenceKey = getCompletionOccurrenceKey(selectedJob);
    const recurring = isRecurringJob(selectedJob);
  
    try {
      if (recurring) {
        // Recurring: close ONLY this date. The rest of the series stays open.
        // isOccurrenceClosed() reads completedDates against occurrenceDate, so
        // this single date turns red here while SendInvoices picks it up as
        // its own invoiceable row and Home drops it from the active list.
        await updateDoc(jobRef, {
          completedDates: arrayUnion(occurrenceKey),
          [`completedAtByDate.${occurrenceKey}`]: new Date().toISOString(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(jobRef, {
          status: "Completed",
          completedDates: arrayUnion(occurrenceKey),
          completedAt: serverTimestamp(),
        });
      }
  
      // ✅ update local UI
      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id
            ? {
                ...j,
                status: recurring ? j.status : "Completed",
                completedDates: [...(j.completedDates || []), occurrenceKey],
                completedAt: recurring ? j.completedAt : new Date().toISOString(),
              }
            : j
        )
      );
  
      closeModal();
      closeVerificationPopup();

      // Reload so every other page reflects the change. View + date are in
      // sessionStorage, so we come straight back to this same calendar view.
      refreshKeepingView();
    } catch (err) {
      console.error("Error completing job:", err);
      alert("Could not mark job completed.");
    }
  };
  

  const addSupervisorNote = async () => {
    if (!selectedJob?.id) return;

    const text = window.prompt("Add supervisor note:");
    if (!text || !text.trim()) return;

    const isRecurring = isRecurringJob(selectedJob);
    const occurrenceKey = isRecurring ? getOccurrenceDateKey(selectedJob) : null;

    const newSupervisorNote = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: text.trim(),
      author: userFullName || "User",
      createdAt: new Date().toISOString(),
      occurrenceDate: occurrenceKey,
    };

    const updated = [
      ...(Array.isArray(selectedJob.supervisorNotes)
        ? selectedJob.supervisorNotes
        : []),
      newSupervisorNote,
    ];

    await updateDoc(doc(db, "jobs", selectedJob.id), {
      supervisorNotes: updated,
    });

    setJobs((prev) =>
      prev.map((job) =>
        job.id === selectedJob.id
          ? { ...job, supervisorNotes: updated }
          : job
      )
    );

    setSelectedJob((prev) => ({
      ...prev,
      supervisorNotes: updated,
    }));
  };

  const continueJob = async () => {
    if (!selectedJob?.id) return;

    const nextDate = window.prompt(
      "Enter the new service date (YYYY-MM-DD):",
      moment(selectedJob.startDate).add(1, "day").format("YYYY-MM-DD")
    );

    if (!nextDate) return;

    const copiedJob = {
      ...selectedJob,
      startDate: nextDate,
      endDate: nextDate,
      recurrence: { type: "none" },
      status: "Active",
      completedDates: [],
      invoicedDates: [],
      occurrenceNotes: {},
      previousJobId: selectedJob.id,
      materialsGroupId: getMaterialsGroupId(selectedJob),
      createdAt: new Date().toISOString(),
    };

    if (copiedJob.jobPlan) {
      copiedJob.jobPlan = { ...copiedJob.jobPlan };
      delete copiedJob.jobPlan.materials;
    }

    delete copiedJob.id;
    delete copiedJob.__renderDate;
    delete copiedJob.occurrenceDate;
    delete copiedJob.originalOccurrenceDate;

    const newRef = await addDoc(collection(db, "jobs"), copiedJob);
    const newJob = { id: newRef.id, ...copiedJob };

    setJobs((prev) => [...prev, newJob]);
    setSelectedJob(newJob);
  };

  const openTotalPopup = () => {
    if (!selectedJob) return;
    setTotalInput(getJobTotal(selectedJob) || "");
    setShowTotalPopup(true);
  };

  const saveTotal = async () => {
    if (!selectedJob || totalInput === "") return;

    const jobKey = String(selectedJob.wo ?? selectedJob.id);

    await addJobTotal({
      jobId: jobKey,
      wo: selectedJob.wo ? String(selectedJob.wo) : null,
      total: Number(totalInput),
      source: "job",
    });

    // Keep the legacy job.estimatedTotal field in sync too, since some
    // other parts of the system (e.g. the invoice-send cloud function)
    // still read/write that field directly.
    await updateDoc(doc(db, "jobs", selectedJob.id), {
      estimatedTotal: Number(totalInput),
    });

    setJobTotals((prev) => ({
      ...prev,
      [jobKey]: Number(totalInput),
    }));

    setJobs((prev) =>
      prev.map((job) =>
        job.id === selectedJob.id
          ? { ...job, estimatedTotal: Number(totalInput) }
          : job
      )
    );

    setSelectedJob((prev) => ({
      ...prev,
      estimatedTotal: Number(totalInput),
    }));

    setShowTotalPopup(false);
  };

  const getMaterialEstimatedCost = (material = {}) => {
    const qty = Number(material.qty || 0);
    const costPer = Number(material.costPer || 0);
    return qty * costPer;
  };

  const normalizeMaterial = (material = {}) => ({
    ...material,
    name: material.name || material.product || "",
    qty: material.qty ?? 1,
    unit: material.unit || "pcs",
    costPer: material.costPer ?? "",
    purchaseLocation: material.purchaseLocation || "",
    notes: material.notes || material.materialNotes || "",
    purchased: Boolean(material.purchased),
    files: Array.isArray(material.files) ? material.files : [],
  });

  const updateMaterialStatus = async (materialIndex, purchased) => {
    if (!selectedJob?.id) return;

    try {
      const groupId = getMaterialsGroupId(selectedJob);
      const currentMaterials = getMaterialsForJob(selectedJob);

      const nextMaterials = currentMaterials.map((material, index) =>
        index === materialIndex
          ? { ...material, purchased: Boolean(purchased) }
          : material
      );

      await saveMaterialsForJob(selectedJob, nextMaterials);

      setJobMaterials((previous) => ({
        ...previous,
        [groupId]: nextMaterials,
      }));

      setMaterialsDraft((previous) =>
        previous.map((material, index) =>
          index === materialIndex
            ? { ...material, purchased: Boolean(purchased) }
            : material
        )
      );
    } catch (error) {
      console.error("Error updating material purchase status:", error);
      alert("Could not update the material purchase status.");
    }
  };

  const openMaterialsPopup = async () => {
    if (!selectedJob) return;
    const groupId = getMaterialsGroupId(selectedJob);
    const currentMaterials =
      jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));

    setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
    setMaterialsDraft(currentMaterials.map(normalizeMaterial));
    setShowMaterialsPopup(true);
  };

  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      normalizeMaterial({}),
    ]);
  };

  const uploadMaterialFiles = async (materialIndex, fileList) => {
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];

    for (const file of files) {
      const fileRef = ref(
        getStorage(),
        `materialFiles/${groupId}/${Date.now()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      uploaded.push({ url, name: file.name });
    }

    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? { ...material, files: [...(material.files || []), ...uploaded] }
          : material
      )
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? {
              ...material,
              files: (material.files || []).filter((_, fi) => fi !== fileIndex),
            }
          : material
      )
    );
  };

  const saveMaterials = async () => {
    if (!selectedJob?.id) return;

    const groupId = getMaterialsGroupId(selectedJob);

    await saveMaterialsForJob(selectedJob, materialsDraft);

    setJobMaterials((previous) => ({
      ...previous,
      [groupId]: materialsDraft,
    }));

    setShowMaterialsPopup(false);
  };

  const openReceiptPopup = () => {
    setReceiptAmount("");
    setReceiptNote("");
    setReceiptFile(null);
    setShowReceiptPopup(true);
  };

  const saveReceipt = async () => {
    if (!selectedJob?.id || !receiptAmount) return;

    setSavingReceipt(true);

    try {
      let fileURL = "";
      let fileName = "";

      if (receiptFile) {
        const receiptRef = ref(
          getStorage(),
          `receipts/${selectedJob.id}/${Date.now()}_${receiptFile.name}`
        );
        await uploadBytes(receiptRef, receiptFile);
        fileURL = await getDownloadURL(receiptRef);
        fileName = receiptFile.name;
      }

      const receipt = {
        amount: Number(receiptAmount),
        note: receiptNote.trim(),
        fileURL,
        fileName,
        createdAt: new Date().toISOString(),
        addedBy: userFullName || "User",
      };

      const receipts = [
        ...(Array.isArray(selectedJob.receipts) ? selectedJob.receipts : []),
        receipt,
      ];

      const receiptsTotal = receipts.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      await updateDoc(doc(db, "jobs", selectedJob.id), {
        receipts,
        receiptsTotal,
      });

      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, receipts, receiptsTotal }
            : job
        )
      );

      setSelectedJob((prev) => ({
        ...prev,
        receipts,
        receiptsTotal,
      }));

      setShowReceiptPopup(false);
    } finally {
      setSavingReceipt(false);
    }
  };

  const saveQuickJobFields = async (changes) => {
    if (!selectedJob?.id) return;
    const result = await saveRecurringJobEdit({ originalJob: selectedJob, editedJob: { ...selectedJob, ...changes } });
    if (result.mode === "cancel") return false;
    let nextSelectedJob = { ...selectedJob, ...changes };
    if (result.mode === "future") {
      setJobs((prev) => [...prev.map((job) => job.id === result.oldJob.id ? result.oldJob : job), result.newJob]);
      nextSelectedJob = result.newJob;
    } else if (result.mode === "occurrence") {
      setJobs((prev) => prev.map((job) => job.id === result.masterJob.id ? result.masterJob : job));
      nextSelectedJob = result.updatedJob;
    } else {
      setJobs((prev) => prev.map((job) => job.id === result.updatedJob.id ? result.updatedJob : job));
      nextSelectedJob = result.updatedJob;
    }
    setSelectedJob(nextSelectedJob);
    setEditedJob(nextSelectedJob);
    return true;
  };

  const deleteSelectedJob = async () => {
    if (!selectedJob?.id) return;

    try {
      const result = await closeOrDeleteJob({ job: selectedJob });

      if (result.mode === "cancel") return;

      if (result.mode === "deleted") {
        setJobs((prev) => prev.filter((job) => job.id !== result.jobId));
        closeModal();
        return;
      }

      // "occurrence" or "future" - job document still exists, just updated.
      setJobs((prev) =>
        prev.map((job) => (job.id === result.updatedJob.id ? { ...job, ...result.updatedJob } : job))
      );
      closeModal();
    } catch (error) {
      console.error("Error closing job:", error);
      alert("Could not close this job. Please try again.");
    }
  };

  const normalizeTechAssign = (value) => {
    if (Array.isArray(value)) return value.length ? value : [""];
    if (typeof value === "string" && value.trim()) {
      return value
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
    }
    return [""];
  };

  const cleanTechAssign = (value) =>
    normalizeTechAssign(value).map((name) => name.trim()).filter(Boolean);

  const addEditedTechnicianField = () => {
    setEditedJob((prev) => ({
      ...prev,
      techAssign: [...normalizeTechAssign(prev.techAssign), ""],
    }));
  };

  const removeEditedTechnicianField = (indexToRemove) => {
    setEditedJob((prev) => {
      const nextTechs = normalizeTechAssign(prev.techAssign).filter(
        (_, index) => index !== indexToRemove
      );

      return {
        ...prev,
        techAssign: nextTechs.length ? nextTechs : [""],
      };
    });
  };

  const updateEditedTechnicianField = (indexToUpdate, value) => {
    setEditedJob((prev) => {
      const nextTechs = normalizeTechAssign(prev.techAssign);
      nextTechs[indexToUpdate] = value;

      return {
        ...prev,
        techAssign: nextTechs,
      };
    });
  };

  const handleEditClick = () => {
    if (!selectedJob) return;

    setEditedJob({
      ...selectedJob,
      techAssign: normalizeTechAssign(selectedJob.techAssign),
      startTimeMode:
        selectedJob.startTime && /^\d{2}:\d{2}$/.test(selectedJob.startTime)
          ? "custom"
          : "preset",
    });

    setIsEditing(true);
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files)); // Update to handle multiple files
  };

  const handleSiteNameChange = (e) => {
    const selectedSiteName = e.target.value;
    const selectedProperty = propertiesList.find(property => property.propertyName === selectedSiteName);

    if (selectedProperty) {
      setEditedJob({
        ...editedJob,
        siteName: selectedSiteName,
        address: selectedProperty.address || '',
        city: selectedProperty.city || '',
        state: selectedProperty.state || '',
      });
    } else {
      setEditedJob({
        ...editedJob,
        siteName: selectedSiteName,
        address: '',
        city: '',
        state: '',
      });
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (!selectedJob || !editedJob?.id) return;

      const jobToSave = {
        ...editedJob,
        techAssign: cleanTechAssign(editedJob.techAssign),
      };

      const result = await saveRecurringJobEdit({
        originalJob: selectedJob,
        editedJob: jobToSave,
      });

      if (result.mode === "cancel") return;

      if (result.mode === "future") {
        setJobs((prevJobs) => [
          ...prevJobs.map((job) =>
            job.id === result.oldJob.id ? result.oldJob : job
          ),
          result.newJob,
        ]);
        setSelectedJob(result.newJob);
      } else if (result.mode === "occurrence") {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.masterJob.id ? result.masterJob : job
          )
        );
        setSelectedJob(result.updatedJob);
      } else {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.updatedJob.id ? result.updatedJob : job
          )
        );
        setSelectedJob(result.updatedJob);
      }

      setIsEditing(false);
    } catch (error) {
      console.error("Error saving job changes:", error);
      alert("Could not save job changes.");
    }
  };

  const getNotesForSelectedJob = (job) => {
    if (!job) return [];

    if (isRecurringJob(job)) {
      const occurrenceKey = getOccurrenceDateKey(job);
      return job.occurrenceNotes?.[occurrenceKey] || [];
    }

    return Array.isArray(job.notes) ? job.notes : [];
  };

  const saveNote = async () => {
    if (
      !selectedJob ||
      (noteText.trim() === "" && selectedFiles.length === 0)
    ) {
      return;
    }

    setUploading(true);

    try {
      const jobId = selectedJob.id;
      const occurrenceKey = getOccurrenceDateKey(selectedJob);
      const recurring = isRecurringJob(selectedJob);

      let fileUploads = [];

      if (selectedFiles.length > 0) {
        const storage = getStorage();

        fileUploads = await Promise.all(
          selectedFiles.map(async (file, index) => {
            const folderKey = recurring ? occurrenceKey : "main";
            const fileRef = ref(
              storage,
              `notes/${jobId}/${folderKey}/${Date.now()}_${index}_${file.name}`
            );

            await uploadBytes(fileRef, file);

            return {
              fileURL: await getDownloadURL(fileRef),
              fileName: file.name,
            };
          })
        );
      }

      const newNote = {
        text: noteText.trim(),
        timestamp: new Date().toLocaleString(),
        author: userFullName || "User",
        files: fileUploads,
        occurrenceDate: recurring ? occurrenceKey : null,
      };

      const jobRef = doc(db, "jobs", jobId);
      const freshSnapshot = await getDoc(jobRef);
      const freshJob = freshSnapshot.exists() ? freshSnapshot.data() : {};

      let updatedJobData;
      let noteIndex;

      if (recurring) {
        const existingOccurrenceNotes = freshJob.occurrenceNotes || {};
        const notesForOccurrence =
          existingOccurrenceNotes[occurrenceKey] || [];

        const updatedNotesForOccurrence = [
          ...notesForOccurrence,
          newNote,
        ];

        const updatedOccurrenceNotes = {
          ...existingOccurrenceNotes,
          [occurrenceKey]: updatedNotesForOccurrence,
        };

        noteIndex = updatedNotesForOccurrence.length - 1;

        await updateDoc(jobRef, {
          occurrenceNotes: updatedOccurrenceNotes,
        });

        updatedJobData = {
          ...freshJob,
          occurrenceNotes: updatedOccurrenceNotes,
        };
      } else {
        const existingNotes = Array.isArray(freshJob.notes)
          ? freshJob.notes
          : [];

        const updatedNotes = [...existingNotes, newNote];
        noteIndex = updatedNotes.length - 1;

        await updateDoc(jobRef, {
          notes: updatedNotes,
        });

        updatedJobData = {
          ...freshJob,
          notes: updatedNotes,
        };
      }

      const refreshedSelectedJob = {
        ...selectedJob,
        ...updatedJobData,
        id: jobId,
        __renderDate: selectedJob.__renderDate,
        occurrenceDate: selectedJob.occurrenceDate,
      };

      // Update the open popup immediately.
      setSelectedJob(refreshedSelectedJob);

      // Update all calendar copies of the job.
      setJobs((previousJobs) =>
        previousJobs.map((job) =>
          job.id === jobId
            ? { ...job, ...updatedJobData }
            : job
        )
      );

      // Send the same notification used by Home.
      try {
        await sendJobNoteEmailCallable({
          jobId,
          noteIndex,
          occurrenceDate: recurring ? occurrenceKey : null,
        });
      } catch (emailError) {
        console.error(
          "Note saved, but note email failed:",
          emailError
        );
      }

      setNoteText("");
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (error) {
      console.error("Error saving note:", error);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };


  const openJobDetails = (job, editMode = false) => {
    setSelectedJob(job);
    setIsEditing(editMode);
  
    if (editMode) {
      setEditedJob({
        ...job,
        techAssign: Array.isArray(job.techAssign)
          ? job.techAssign[0] || ""
          : job.techAssign || "",
        startTimeMode: job.startTime?.includes(":") ? "custom" : "preset",
      });
    }
  };

  const selectedDayKey = moment(selectedDate).format("YYYY-MM-DD");

const jobsForSelectedDay = useMemo(() => {
  return jobsByDate[selectedDayKey] || [];
}, [jobsByDate, selectedDayKey]);

  return (
    <div>
      <h1>Calendar</h1>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
      <div className="modal-buttons">
        <button className="rose-button" onClick={() => setView('month')}>Month View</button>
        <button className="teal-button" onClick={() => setView('week')}>Week View</button>
        <button className="plum-button" onClick={() => setView('day')}>Day View</button>
        {["Admin", "Manager", "Assistant"].includes(userRole) && (
          <button
            className="green-button"
            onClick={() => setView('availability')}
          >
            Availability
          </button>
        )}
      </div>
</div>

<div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: "16px",
          padding: "12px 14px",
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          borderRadius: "10px",
          alignItems: "center",
        }}
      >
        <strong style={{ color: "#7c2d12", marginRight: 6 }}>Calendar Legend:</strong>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: "#6FAE7C", display: "inline-block" }} />
          <span>Proposal</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: "#D9B95B", display: "inline-block" }} />
          <span>Meeting / Inspection</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: "#8E6BBE", display: "inline-block" }} />
          <span>Action Item</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: "#5B8FD9", display: "inline-block" }} />
          <span>All Other Jobs</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: "#D96C6C", display: "inline-block" }} />
          <span>Completed / Invoice Sent</span>
        </div>
      </div>

      {/* Month View */}
      {view === 'month' && (
        <div>
                   <Calendar
  value={selectedDate}
  onChange={setSelectedDate}
  locale="en-US"
  calendarType="gregory"
  tileContent={({ date, view }) => {
    if (view === 'month') {
      const dateKey = moment(date).format("YYYY-MM-DD");
const jobsForDay = jobsByDate[dateKey] || [];
const timeOffForDay = timeOffByDate[dateKey] || [];

      return (
        <>
         {jobsForDay.map((job, index) => (
            <div
            key={getJobRenderKey(job, index)}
              style={getJobBarStyle(job)}
              className="azul-button"
              onClick={(e) => {
                e.stopPropagation();
                openJobDetails(job, false);
              }}
              title={`Site: ${job.siteName}
City: ${job.city}
Scope: ${job.scope}
Tech: ${job.techAssign}`}
            >
                            {job.wo && (
                              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>
                                WO# {job.wo}
                              </div>
                            )}
                            {getJobDisplayText(job)}
                            <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    openJobDetails(job, true);
  }}
  style={{
    marginTop: 4,
    padding: "3px 6px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  }}
>
  Edit
</button>
            </div>
          ))}
          {timeOffForDay.map((request) => (
            <div
              key={request.id}
              style={getTimeOffBarStyle(request)}
              onClick={() => setSelectedTimeOffRequest(request)}
              title={`Name: ${request.name}
Dates: ${request.startDate} - ${request.endDate}
Reason: ${request.reason}`}
            >
              {request.name}: {request.startDate} - {request.endDate}
            </div>
          ))}
        </>
      );
    }
  }}
/>

        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div>
          <div>
            <h2>
            Week of {getWeekStart(selectedDate).format('MMM DD')} -{' '}
            {getWeekEnd(selectedDate).format('MMM DD, YYYY')}
            </h2>
            <button className="blue-button" onClick={() => toggleWeek('prev')}>Previous Week</button>&nbsp;&nbsp;
            <button className="blue-button" onClick={() => toggleWeek('next')}>Next Week</button>&nbsp;&nbsp;
            <button className="blue-button" onClick={goToToday}>Today</button>
          </div>
          <div>
          {Array.from({ length: 7 }, (_, i) =>
              getWeekStart(selectedDate).clone().add(i, 'days').format('YYYY-MM-DD')
            ).map((date) => (
              <div key={date} style={{ border: '1px solid #ddd', marginBottom: '20px' }}>
                <h3>{moment(date).format('ddd, MMM DD')}</h3>
                <ul>
                {(jobsByDate[date] || []).map((job, index) => (
                      <li
                      key={getJobRenderKey(job, index)}
                      style={{
                        background: getJobTileColor(job),
                        color: "#fff",
                        padding: "10px",
                        borderRadius: "8px",
                        marginBottom: "8px",
                        opacity: isOccurrenceClosed(job) ? 0.95 : 1,
                      }}
                    >
                      <strong>WO#:</strong> {job.wo || "—"}<br />
                      <strong>Site Name:</strong> {getJobDisplayText(job)}<br />
                      <strong>Start Time:</strong> {job.startTime}<br />
                      <strong>City:</strong> {job.city}<br />
                      <strong>Scope:</strong> {job.scope}<br />
                      <strong>Tech:</strong> {job.techAssign}<br />
                      <button
  className="blue-button"
  onClick={(e) => {
    e.stopPropagation();
    openJobDetails(job, false);
  }}
>
  Details
</button>

<button
  className="grey-button"
  onClick={(e) => {
    e.stopPropagation();
    openJobDetails(job, true);
  }}
>
  Edit Job
</button>
                    </li>
                    ))}
                 {(timeOffByDate[date] || []).map((request) => (
                      <li key={request.id} style={getTimeOffBarStyle(request)}>
                        <strong>{request.name}:</strong> {request.startDate} - {request.endDate}<br />
                        <button className="red-button" onClick={() => setSelectedTimeOffRequest(request)}>Details</button>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Day View */}
      {view === 'day' && (
        <div>
          <div>
            <h2>{moment(selectedDate).format('dddd, MMMM DD, YYYY')}</h2>
            <button className="blue-button" onClick={() => toggleDay('prev')}>Previous Day</button>&nbsp;&nbsp;
            <button className="blue-button" onClick={() => toggleDay('next')}>Next Day</button>&nbsp;&nbsp;
            <button className="blue-button" onClick={goToToday}>Today</button>
          </div>
          <div>
            {Array.from({ length: 48 }, (_, i) =>
              moment(selectedDate).startOf('day').add(i * 30, 'minutes').format('HH:mm')
            ).map((time) => (
              <div key={time} style={{ border: '1px solid #ddd', padding: '10px' }}>
                <strong>{moment(time, 'HH:mm').format('hh:mm A')}</strong>
                <ul>
                {jobsForSelectedDay
  .filter((job) => {
  
  
    // Show timed jobs in matching slot
    if (/^\d{2}:\d{2}$/.test(job.startTime || "")) {
      return job.startTime === time;
    }
  
    // Show ANY / window-based jobs at 8:00 AM block
    if (
      !job.startTime ||
      job.startTime === "ANY" ||
      job.startTime.includes("AM") ||
      job.startTime.includes("PM")
    ) {
      return time === "08:00";
    }
  
    return false;
  })
  .map((job, index) => (
                      <li
                      key={getJobRenderKey(job, index)}
                      style={{
                        background: getJobTileColor(job),
                        color: "#fff",
                        padding: "10px",
                        borderRadius: "8px",
                        marginBottom: "8px",
                        opacity: isOccurrenceClosed(job) ? 0.95 : 1,
                      }}
                    >
                      <strong>{getJobDisplayText(job)}</strong><br />
                      Work Order: {job.wo}<br />
                      Tech: {job.techAssign}<br />
                      <button
  onClick={(e) => {
    e.stopPropagation();
    openJobDetails(job, false);
  }}
>
  Details
</button>

<button
  onClick={(e) => {
    e.stopPropagation();
    openJobDetails(job, true);
  }}
>
  Edit Job
</button>
                    </li>
                    ))}
                {(timeOffByDate[moment(selectedDate).format("YYYY-MM-DD")] || []).map((request) => (
                      <li key={request.id} style={getTimeOffBarStyle(request)}>
                        <strong>{request.name}:</strong> {request.startDate} - {request.endDate}<br />
                        <button className="red-button" onClick={() => setSelectedTimeOffRequest(request)}>Details</button>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Availability View */}
      {view === 'availability' && ["Admin", "Manager", "Assistant"].includes(userRole) && (
        <AvailabilityView
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          jobsByDate={jobsByDate}
          timeOffByDate={timeOffByDate}
          usersList={usersList}
          onOpenJob={(job) => job && openJobDetails(job, false)}
          onScheduleSlot={({ dateKey, startTime, techName }) =>
            setScheduleDraft({
              startDate: dateKey,
              endDate: dateKey,
              startTime,
              techAssign: techName ? [techName] : [],
            })
          }
        />
      )}

      {/* Schedule Job popup — the real Scheduling form, opened from an open block */}
      {scheduleDraft && (
        <Scheduling
          key={`${scheduleDraft.startDate}-${scheduleDraft.startTime}-${(scheduleDraft.techAssign || []).join(",")}`}
          scheduleOnly
          prefill={scheduleDraft}
          onClose={() => setScheduleDraft(null)}
          onScheduled={(newJob) =>
            setJobs((prev) => [...prev.filter((j) => j.id !== newJob.id), newJob])
          }
        />
      )}

      {/* Job Details Popup */}
      {selectedJob && createPortal(
        <>
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
              width: "min(1000px, calc(100vw - 40px))",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              padding: 20,
              borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
              textAlign: "left",
              boxSizing: "border-box",
              zIndex: 999999,
            }}
          >
            <h2>Job Details</h2>

  <QuickJobFieldEditor job={selectedJob} usersList={usersList} onSave={saveQuickJobFields} />
            {isEditing ? (
              <>
                <label>
                  Site Name:
                  <select
                    name="siteName"
                    value={editedJob.siteName || ''}
                    onChange={handleSiteNameChange}
                    required
                  >
                    <option value="">Select Site Name</option>
                    {propertiesList
                      .sort((a, b) => a.propertyName.localeCompare(b.propertyName)) // Sort properties alphabetically
                      .map((property) => (
                        <option key={property.id} value={property.propertyName}>
                          {property.propertyName}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Address:
                  <input
                    type="text"
                    name="address"
                    value={editedJob.address || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  City:
                  <input
                    type="text"
                    name="city"
                    value={editedJob.city || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  State:
                  <input
                    type="text"
                    name="state"
                    value={editedJob.state || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  Start Date:
                  <input
                    type="date"
                    name="startDate"
                    value={editedJob.startDate || ''}
                    onChange={handleInputChange}
                    required
                  />
                </label>
                <label>
                  End Date:
                  <input
                    type="date"
                    name="endDate"
                    value={editedJob.endDate || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>Start Time:</label>
<select
  name="startTime"
  value={editedJob.startTime ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value !== "Custom Time") {
      setEditedJob((prev) => ({
        ...prev,
        startTime: value,
        startTimeMode: "preset",
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        startTime: "",
        startTimeMode: "custom",
      }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((opt) => (
    <option key={opt} value={opt}>{opt}</option>
  ))}
</select>

{/* Show only when Custom Time is selected */}
{editedJob.startTimeMode === "custom" && (
  <input
    type="time"
    value={editedJob.startTime || ""}
    onChange={(e) =>
      setEditedJob((prev) => ({ ...prev, startTime: e.target.value }))
    }
  />
)}
<label>End Time:</label>
<input
  type="time"
  name="endTime"
  value={editedJob.endTime || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({ ...prev, endTime: e.target.value }))
  }
/>

                <label>
                  Service Type:
                  <select
                    name="serviceType"
                    value={editedJob.serviceType || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Service Type</option>
                    {[
                      "Proposal",
                      "Meeting",
                      "Construction",
                      "Maintenance",
                      "Action Items",
                      "Cleaning",
                      "Bulk Trash",
                      "Inspection",
                      "Electrical",
                      "Plumbing",
                      "HVAC",
                      "Concrete",
                    ].map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Scope:
                  <input
                    type="text"
                    name="scope"
                    value={editedJob.scope || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Job Description:
                  <textarea
                    name="jobDescription"
                    value={editedJob.jobDescription || ''}
                    onChange={handleInputChange}
                  ></textarea>
                </label>
                <label>
  Client Phone:
  <input
    type="text"
    name="clientPhone"
    value={editedJob.clientPhone || ''}
    onChange={handleInputChange}
  />
</label>

<label>
  Client Email:
  <input
    type="email"
    name="clientEmail"
    value={editedJob.clientEmail || ''}
    onChange={handleInputChange}
  />
</label>

                <label>
                  Status:
                  <select
                    name="status"
                    value={editedJob.status || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Status</option>
                    {["Active", "On Hold", "Completed", "Invoice Sent", "Proposal Sent", "Monthly"].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                    Tech Assign:
                  </label>

                  {normalizeTechAssign(editedJob.techAssign).map((tech, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <select
                        value={tech}
                        onChange={(e) =>
                          updateEditedTechnicianField(index, e.target.value)
                        }
                        style={{ flex: 1 }}
                      >
                        <option value="">Select Technician</option>
                        {usersList.map((user) => (
                          <option
                            key={user.id}
                            value={`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                          >
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="red-button"
                        onClick={() => removeEditedTechnicianField(index)}
                        style={{
                          width: 44,
                          minWidth: 44,
                          height: 44,
                          padding: 0,
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                        title="Remove technician"
                      >
                        X
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="blue-button"
                    onClick={addEditedTechnicianField}
                  >
                    Add Technician
                  </button>
                </div>
                <div className="modal-buttons">
                  <button className="green-button" onClick={handleSaveChanges}>
                    Save
                  </button>
                  <button
                    className="red-button"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedJob({});
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
                <p><strong>Work Order:</strong> {selectedJob.wo}</p>
                <p><strong>Address:</strong> {selectedJob.address}</p>
                <p><strong>City:</strong> {selectedJob.city}</p>
                <p><strong>State:</strong> {selectedJob.state}</p>
                <p><strong>Start Date:</strong> {selectedJob.startDate}</p>
                <p><strong>End Date:</strong> {selectedJob.endDate}</p>
                <p><strong>Start Time:</strong> {selectedJob.startTime || "N/A"}</p>
<p><strong>End Time:</strong> {selectedJob.endTime || "N/A"}</p>
                <p><strong>Service Type:</strong> {selectedJob.serviceType}</p>
                <p><strong>Scope:</strong> {selectedJob.scope}</p>
                <p><strong>Job Description:</strong> {selectedJob.jobDescription}</p>
                <p><strong>Client Phone:</strong> {selectedJob.clientPhone || "N/A"}</p>
<p><strong>Client Email:</strong> {selectedJob.clientEmail || "N/A"}</p>

                <p><strong>Tech Assign:</strong> {selectedJob.techAssign}</p>
                <p><strong>Status:</strong> {selectedJob.status}</p>
{/* ===== Materials ===== */}
<h3 style={{ marginTop: 16 }}>Materials</h3>

{getMaterialsForJob(selectedJob).length > 0 ? (
  <div style={{ display: "grid", gap: 10 }}>
    {getMaterialsForJob(selectedJob).map((material, index) => {
      const estimatedCost = getMaterialEstimatedCost(material);

      return (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: "42px minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
            padding: 12,
            border: material.purchased
              ? "2px solid #16a34a"
              : "1px solid #cbd5e1",
            borderRadius: 10,
            background: material.purchased ? "#f0fdf4" : "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={() => updateMaterialStatus(index, !material.purchased)}
            aria-label={
              material.purchased
                ? "Mark material as not purchased"
                : "Mark material as purchased"
            }
            title={
              material.purchased
                ? "Purchased — click to undo"
                : "Not purchased — click when purchased"
            }
            className={`material-toggle${material.purchased ? " material-toggle--checked" : ""}`}
          >
            <svg
              className="material-toggle__check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 12 9 17 20 6" />
            </svg>
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>
              {material.name || material.product || "Unnamed Material"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 6,
                marginTop: 8,
                fontSize: 14,
              }}
            >
              <div>
                <strong>Qty:</strong> {material.qty ?? ""}
                {material.unit ? ` ${material.unit}` : ""}
              </div>
              <div>
                <strong>Cost Per:</strong>{" "}
                {Number(material.costPer || 0).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div>
                <strong>Estimated Material Cost:</strong>{" "}
                {estimatedCost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div>
                <strong>Purchase Location:</strong>{" "}
                {material.purchaseLocation || "Not entered"}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                {material.purchased ? "Purchased" : "Not Purchased"}
              </div>
            </div>

            {material.notes && (
              <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                <strong>Material Notes:</strong> {material.notes}
              </div>
            )}

            {Array.isArray(material.files) && material.files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {material.files.map((file, fileIndex) => (
                  <a
                    key={fileIndex}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.name}
                  >
                    {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 60,
                          height: 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          background: "#fff",
                          fontSize: 10,
                          textAlign: "center",
                          padding: 4,
                          overflow: "hidden",
                        }}
                      >
                        {file.name}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}

    <div
      style={{
        padding: 10,
        borderRadius: 8,
        background: "#eff6ff",
        border: "1px solid #93c5fd",
        fontWeight: 900,
      }}
    >
      Total Estimated Material Cost:{" "}
      {getMaterialsForJob(selectedJob)
        .reduce(
          (sum, material) => sum + getMaterialEstimatedCost(material),
          0
        )
        .toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}
    </div>
  </div>
) : (
  <p style={{ fontStyle: "italic" }}>No materials listed.</p>
)}

                <h3>Notes</h3>
                {getNotesForSelectedJob(selectedJob).length > 0 ? (
                  <ul>
                    {getNotesForSelectedJob(selectedJob).map((note, index) => (
                      <li key={`${note.timestamp || "note"}-${index}`}>
                        <p><strong>Note:</strong> {note.text}</p>
                        <p><em>Added By:</em> {note.author}</p> {/* Display the author's name */}
                        <p><em>Timestamp:</em> {note.timestamp}</p>
                        {note.files && note.files.length > 0 && (
                          <ul>
                            {note.files.map((file, fileIndex) => (
                              <li key={fileIndex}>
                                <a href={file.fileURL} target="_blank" rel="noopener noreferrer">{file.fileName}</a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No notes available.</p>
                )}

                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
                  <div>
                    <h3>Uploaded Files</h3>
                    <div className="uploaded-files">
                      {selectedJob.fileURLs.map((fileURL, index) => (
                        <div key={index}>
                          <a href={fileURL} target="_blank" rel="noopener noreferrer">
                            {fileURL.match(/\.(jpeg|jpg|gif|png)$/) ? (
                              <img src={fileURL} alt={`File ${index + 1}`} className="thumbnail" />
                            ) : (
                              `View File ${index + 1}`
                            )}
                          </a>
                          <p>{selectedJob.fileNames && selectedJob.fileNames[index]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <CollapsibleActionSection title="Supervisor" tone="supervisor" defaultOpen={false}>
                  <button className="grey-button" onClick={handleEditClick}>
                    Edit
                  </button>
                  <button className="grey-button" onClick={addSupervisorNote}>
                    Add Supervisor Note
                  </button>
                  {!isRecurringJob(selectedJob) && selectedJob.status !== "Completed" && (
                    <button className="yellow-button" onClick={continueJob}>
                      Continue
                    </button>
                  )}
                  <button className="green-button" onClick={openTotalPopup}>
                    {getJobTotal(selectedJob) ? "Change Total" : "Add Total"}
                  </button>
                  <button className="black-button" onClick={deleteSelectedJob}>
                    Close Job
                  </button>
                </CollapsibleActionSection>

                <div className="modal-buttons">
                  <button className="blue-button" onClick={() => setShowNotePopup(true)}>
                    Add Note
                  </button>
                  <button
                    className="green-button"
                    onClick={async () => {
                      // Techs must check in at the job before completing it.
                      if (userRole === "Tech") {
                        const checkedIn = await userHasVisitForJob(currentUid, selectedJob).catch(() => false);
                        if (!checkedIn) {
                          alert(
                            "Check in to this job first.\n\nOpen the job on the Home page and tap Check In when you arrive, then mark it completed."
                          );
                          return;
                        }
                      }
                      // Completing a job opens the Complete Job & Create Invoice form.
                      setShowInvoiceModal(true);
                    }}
                  >
                    Mark Job as Completed
                  </button>
                </div>

                <div className="modal-buttons">
                  <button className="blue-button" onClick={openReceiptPopup}>
                    Add Receipt
                  </button>
                  <button className="blue-button" onClick={openMaterialsPopup}>
                    Add / Edit Materials
                  </button>
                  <button className="grey-button" onClick={() => setShowHistoryPopup(true)}>
                    History
                  </button>
                  <button
                    className="yellow-button"
                    onClick={() => navigate(`/JobChecklist/${selectedJob.id}`)}
                  >
                    Checklist
                  </button>
                  <button className="red-button" onClick={closeModal}>
                    Close
                  </button>
                </div>

                {showHistoryPopup && (
                  <JobHistoryPopup
                    job={selectedJob}
                    jobs={jobs}
                    onClose={() => setShowHistoryPopup(false)}
                  />
                )}

                {showInvoiceModal && selectedJob && (
                  <JobCompletionInvoiceModal
                    job={selectedJob}
                    onClose={() => {
                      setShowInvoiceModal(false);
                      if (completionSubmitted) {
                        setCompletionSubmitted(false);
                        setSelectedJob(null);
                        // Reload so Home / SendInvoices / Job History pick it up.
                        refreshKeepingView();
                      }
                    }}
                    onCompleted={(invoice) => {
                      setCompletionSubmitted(true);
                      const recurring = isRecurringJob(selectedJob);
                      const occurrenceKey = getCompletionOccurrenceKey(selectedJob);

                      setJobs((prev) =>
                        prev.map((j) =>
                          j.id === selectedJob.id
                            ? {
                                ...j,
                                status: recurring ? j.status : "Completed",
                                completedDates: recurring
                                  ? [...(j.completedDates || []), occurrenceKey]
                                  : j.completedDates,
                                approvedInvoiceId: invoice.id,
                                invoiceStatus: invoice.status,
                              }
                            : j
                        )
                      );

                      setSelectedJob((prev) =>
                        prev
                          ? {
                              ...prev,
                              status: recurring ? prev.status : "Completed",
                              approvedInvoiceId: invoice.id,
                              invoiceStatus: invoice.status,
                            }
                          : prev
                      );
                    }}
                  />
                )}
              </>
            )}
          </div>
        </>,
        document.body
      )}

      {showMaterialsPopup && createPortal(
  <>
    <div
      onClick={() => setShowMaterialsPopup(false)}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.55)",
        zIndex: 1999998,
      }}
    />
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        margin: 0,
        width: "min(1050px, 96vw)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        background: "#fff",
        padding: 20,
        borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
        textAlign: "left",
        boxSizing: "border-box",
        zIndex: 1999999,
      }}
    >
      <h3 style={{ marginTop: 0 }}>Add / Edit Materials</h3>

      {materialsDraft.length === 0 && (
        <p style={{ fontStyle: "italic" }}>No materials have been added.</p>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {materialsDraft.map((material, index) => {
          const estimatedCost = getMaterialEstimatedCost(material);

          const updateDraftMaterial = (patch) => {
            setMaterialsDraft((previous) =>
              previous.map((item, itemIndex) =>
                itemIndex === index ? { ...item, ...patch } : item
              )
            );
          };

          return (
            <div
              key={index}
              style={{
                padding: 14,
                border: material.purchased
                  ? "2px solid #16a34a"
                  : "1px solid #cbd5e1",
                borderRadius: 12,
                background: material.purchased ? "#f0fdf4" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <strong>Material {index + 1}</strong>
                <button
                  type="button"
                  className="red-button"
                  onClick={() =>
                    setMaterialsDraft((previous) =>
                      previous.filter((_, itemIndex) => itemIndex !== index)
                    )
                  }
                  style={{ width: "auto" }}
                >
                  Remove Material
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 12,
                }}
              >
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Product
                  <input
                    type="text"
                    value={material.name || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ name: event.target.value })
                    }
                    placeholder="Product or material name"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Qty
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={material.qty ?? ""}
                    onChange={(event) =>
                      updateDraftMaterial({ qty: event.target.value })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Unit
                  <input
                    type="text"
                    value={material.unit || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ unit: event.target.value })
                    }
                    placeholder="pcs, ft, bags"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Cost Per
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <span style={{ paddingLeft: 10 }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={material.costPer ?? ""}
                      onChange={(event) =>
                        updateDraftMaterial({ costPer: event.target.value })
                      }
                      style={{ border: 0, boxShadow: "none" }}
                    />
                  </div>
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Estimated Material Cost
                  <input
                    type="text"
                    readOnly
                    value={estimatedCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                    style={{
                      background: "#e2e8f0",
                      fontWeight: 900,
                    }}
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Purchase Location
                  <input
                    type="text"
                    value={material.purchaseLocation || ""}
                    onChange={(event) =>
                      updateDraftMaterial({
                        purchaseLocation: event.target.value,
                      })
                    }
                    placeholder="Home Depot, Lowe's, supplier, etc."
                  />
                </label>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 5,
                  marginTop: 12,
                  fontWeight: 800,
                }}
              >
                Material Notes
                <textarea
                  rows="3"
                  value={material.notes || ""}
                  onChange={(event) =>
                    updateDraftMaterial({ notes: event.target.value })
                  }
                  placeholder="Sizes, model numbers, colors, special instructions, etc."
                />
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(material.purchased)}
                  onChange={(event) =>
                    updateDraftMaterial({ purchased: event.target.checked })
                  }
                  style={{ width: 20, height: 20 }}
                />
                Purchased
              </label>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  Photos / Files
                </div>

                {Array.isArray(material.files) && material.files.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {material.files.map((file, fileIndex) => (
                      <div key={fileIndex} style={{ position: "relative" }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                          {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #cbd5e1",
                                borderRadius: 6,
                                background: "#fff",
                                fontSize: 10,
                                textAlign: "center",
                                padding: 4,
                                overflow: "hidden",
                              }}
                            >
                              {file.name}
                            </div>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMaterialFile(index, fileIndex)}
                          title="Remove file"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "1px solid #b91c1c",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="blue-button"
        onClick={addMaterial}
        style={{ marginTop: 14, width: "auto" }}
      >
        + Add Material
      </button>

      <div
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 10,
          border: "2px solid #2563eb",
          background: "#eff6ff",
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        Total Estimated Material Cost:{" "}
        {materialsDraft
          .reduce(
            (sum, material) => sum + getMaterialEstimatedCost(material),
            0
          )
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
      </div>

      <div className="modal-buttons">
        <button type="button" className="green-button" onClick={saveMaterials}>
          Save Materials
        </button>
        <button
          type="button"
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

      {showReceiptPopup && createPortal(
        <>
          <div
            onClick={() => setShowReceiptPopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Add Receipt</h2>
            <label>Amount</label>
            <input
              type="number"
              value={receiptAmount}
              onChange={(e) => setReceiptAmount(e.target.value)}
            />
            <label>Note</label>
            <input
              type="text"
              value={receiptNote}
              onChange={(e) => setReceiptNote(e.target.value)}
            />
            <label>Receipt File</label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveReceipt} disabled={savingReceipt}>
                {savingReceipt ? "Saving..." : "Save Receipt"}
              </button>
              <button className="red-button" onClick={() => setShowReceiptPopup(false)}>Cancel</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {showTotalPopup && createPortal(
        <>
          <div
            onClick={() => setShowTotalPopup(false)}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>{getJobTotal(selectedJob) ? "Change Total" : "Add Total"}</h2>
            <input
              type="number"
              value={totalInput}
              onChange={(e) => setTotalInput(e.target.value)}
            />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveTotal}>Save</button>
              <button className="red-button" onClick={() => setShowTotalPopup(false)}>Cancel</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Note Popup */}
      {showNotePopup && createPortal(
        <>
          <div
            onClick={closeNotePopup}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Add Note</h2>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
            ></textarea>
            <input type="file" multiple onChange={handleFileChange} />
            <div className="modal-buttons">
              <button
                type="button"
                className="green-button"
                onClick={saveNote}
                disabled={uploading}
              >
                {uploading ? <LoadingSpinner label="Saving note..." /> : "Save Note"}
              </button>
              <button
                type="button"
                className="red-button"
                onClick={closeNotePopup}
              >
                Cancel
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Verification Popup */}
      {showVerificationPopup && createPortal(
        <>
          <div
            onClick={closeVerificationPopup}
            style={{
              position: "fixed", inset: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.55)", zIndex: 1999998,
            }}
          ></div>
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)", margin: 0,
              width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
              background: "#fff", padding: 20, borderRadius: 8,
              boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
              boxSizing: "border-box", zIndex: 1999999,
            }}
          >
            <h2>Mark Job as Completed</h2>
            <p>Are you sure you want to mark this job as completed?</p>
            <div className="modal-buttons">
              <button className='green-button' onClick={markJobCompleted}>Yes</button>
              <button className='red-button' onClick={closeVerificationPopup}>No</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Time Off Details Popup */}
      {selectedTimeOffRequest && (
        <TimeOffDetailsModal
          entry={selectedTimeOffRequest}
          onClose={() => setSelectedTimeOffRequest(null)}
        />
      )}
    </div>
  );
};

export default CalendarPage;